import { observer } from 'mobx-react';
import React from 'react';
import { useWrapRequest } from 'use-wrap-request';
import { Alert, AlertDescription } from '@components/ui/alert';
import useInfiniteScroll from '../../../components/hooks/use-scroll';
import { useSearchReducer } from '../../../components/hooks/useSearchReducer';

import { UserContext } from '..';
import { SectionHeading } from '../../../components/section-heading';
import { Spinner } from '../../../components/spinner';
import { Api } from '../../../core/api';
import { ActivityPointsDto } from '../../../core/api/FitnessActivityClient';
import { injectTSDI } from '../../../core/tsdi';
import { ActivityPoints } from '../../../tenant/employee/activity-points/activity-points';

export const UserActivityPoints = observer(() => {
    const user = React.useContext(UserContext);
    const { fitnessActivityClient } = injectTSDI(Api);
    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0,
        sort: '-timestamp'
    });

    const [activityPoints, setActivityPoints] = React.useState<
        ActivityPointsDto[]
    >([]);

    const activityPointRequest = useWrapRequest(
        async (
            searchParams: QueryParamsDto,
            activities: ActivityPointsDto[]
        ) => {
            const result =
                await fitnessActivityClient.admin.activitySupportControllerGetActivityPoints(
                    { userId: `${user.id}`, ...searchParams }
                );
            setActivityPoints([...activities, ...result.data.content]);

            return { ...result.data, content: result.data.content || [] };
        },
        {}
    );

    const page = useInfiniteScroll({
        loading: activityPointRequest.loading,
        distance: 200
    });

    React.useEffect(() => {
        if (
            page > 0 &&
            activityPointRequest.$.totalPages &&
            activityPointRequest.$.totalPages > page
        ) {
            dispatch({ type: 'page', page });
        }
    }, [page]);

    React.useEffect(() => {
        activityPointRequest.request([searchParams, activityPoints]);
    }, [searchParams]);

    const total = activityPoints.reduce(
        (sum, entry) => sum + (entry.value ?? 0),
        0
    );

    return (
        <div>
            {activityPoints.length === 0 && !activityPointRequest.loading && (
                <Alert variant="info">
                    <AlertDescription>
                        No activity points found
                    </AlertDescription>
                </Alert>
            )}
            {activityPoints.length > 0 && (
                <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
                    <SectionHeading
                        title="Activity points"
                        hint={`${activityPoints.length} entries loaded`}
                    />
                    <span className="text-sm text-muted-foreground">
                        {total.toLocaleString()} pts loaded
                    </span>
                </div>
            )}
            {activityPoints.length === 0 && activityPointRequest.loading && (
                <div className="flex min-h-40 items-center justify-center">
                    <Spinner />
                </div>
            )}
            <ul
                className={
                    activityPoints.length === 0
                        ? 'm-0 list-none p-0'
                        : 'm-0 list-none divide-y overflow-hidden rounded-xl border bg-card p-0'
                }
            >
                {activityPoints.map((activityPoints) => (
                    <ActivityPoints
                        key={activityPoints.id}
                        activityPoints={activityPoints}
                    />
                ))}
            </ul>
            {activityPointRequest?.loading && (
                <div className="flex justify-center py-4">
                    <Spinner />
                </div>
            )}
        </div>
    );
});

export default UserActivityPoints;
